import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, Clock, Download, FileText } from 'lucide-react';
import '../styles/pricing.css';
import { PRICING_PACKAGES, BESPOKE, COMPARISON_ROWS, PRICING_FAQS } from '../data/pricingData';
import { BRAND } from '../data/siteData';
import { trackEvent } from '../lib/track';
import PricingSections from './PricingSections';
import PricingEnquiry from './PricingEnquiry';
import { BundleBanner } from './BundleBuilder';
import { BackToTop } from '../components/BackToTop';

const scrollToEnquiry = () => {
  document.querySelector('#enquiry')?.scrollIntoView({ behavior: 'smooth' });
};

function PageHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Website Design Packages & Pricing | Boost! Marketing';

    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : null;
    if (desc) {
      desc.setAttribute(
        'content',
        'Explore professional website design packages from Boost!, with transparent starting prices for business websites, ecommerce stores and bespoke digital experiences.'
      );
    }

    // FAQ structured data for this page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'pricing-faq-schema';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: PRICING_FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(script);

    // Canonical URL (otherwise this page inherits the homepage canonical)
    const canonicalUrl = 'https://boostmarketing.agency/website-design-pricing';
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc !== null) desc.setAttribute('content', prevDesc);
      document.getElementById('pricing-faq-schema')?.remove();
      if (canonical && prevCanonical !== null) canonical.setAttribute('href', prevCanonical);
    };
  }, []);
  return null;
}

function PricingHeader() {
  return (
    <header className="pp-header" data-testid="pricing-header">
      <div className="pp-header-inner">
        <Link to="/" aria-label="BOOST! Design & Marketing home">
          <img src={BRAND.logo} alt="BOOST! Design & Marketing" />
        </Link>
        <div className="pp-header-links">
          <Link to="/" className="pp-header-link">Main site</Link>
          <Link to={{ pathname: '/', hash: '#success' }} className="pp-header-link" data-testid="pricing-header-our-work">Our work</Link>
          <button
            className="pp-btn pp-btn-primary pp-btn-sm"
            onClick={() => { trackEvent('proposal_request_click', { location: 'header' }); scrollToEnquiry(); }}
            data-testid="pricing-header-cta"
          >
            Request a proposal
          </button>
        </div>
      </div>
    </header>
  );
}

const SUBNAV_SECTIONS = [
  { id: 'packages', label: 'Website Design' },
  { id: 'seo-geo', label: 'SEO & GEO' },
  { id: 'paid-media', label: 'Paid Media' },
  { id: 'enquiry', label: 'Get a proposal' },
];

function PricingSubNav() {
  const [active, setActive] = useState('packages');
  useEffect(() => {
    const els = SUBNAV_SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: '-130px 0px -55% 0px', threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    const onScroll = () => { if (window.scrollY < 220) setActive('packages'); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { observer.disconnect(); window.removeEventListener('scroll', onScroll); };
  }, []);
  const jump = (id) => {
    trackEvent('pricing_subnav_click', { section: id });
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <nav className="pp-subnav" data-testid="pricing-subnav">
      <div className="pp-subnav-inner">
        {SUBNAV_SECTIONS.map((s) => (
          <button
            key={s.id}
            className={`pp-subnav-link${active === s.id ? ' pp-subnav-link-active' : ''}`}
            onClick={() => jump(s.id)}
            data-testid={`pricing-subnav-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="pp-hero">
      <div className="pp-container">
        <span className="pp-kicker">Website Design Packages &amp; Pricing</span>
        <h1 className="pp-display" data-testid="pricing-h1">
          WEBSITE DESIGN THAT MOVES YOUR <em>BUSINESS FORWARD.</em>
        </h1>
        <p className="pp-hero-copy">
          Whether you need a focused new business website, a powerful lead-generation platform or a
          complete ecommerce experience, Boost! combines strategy, design, technology and marketing
          insight to create websites built around real business goals.
        </p>
        <p className="pp-hero-copy">
          Our pricing is transparent, our process is collaborative, and every project is tailored to
          the needs of the organisation behind it.
        </p>
        <div>
          <span className="pp-price-statement" data-testid="pricing-statement">
            Professional business websites from <strong>£1,750 + VAT</strong>
          </span>
        </div>
        <div className="pp-hero-ctas">
          <button
            className="pp-btn pp-btn-primary pp-btn-lg"
            onClick={() => { trackEvent('proposal_request_click', { location: 'hero' }); scrollToEnquiry(); }}
            data-testid="pricing-hero-primary"
          >
            Request a website proposal
          </button>
          <button
            className="pp-btn pp-btn-outline pp-btn-lg"
            onClick={() => { trackEvent('discovery_call_click', { location: 'hero' }); scrollToEnquiry(); }}
            data-testid="pricing-hero-secondary"
          >
            Book a discovery call
          </button>
        </div>
        <div className="pp-hero-links" data-testid="pricing-hero-links">
          <a
            className="pp-hero-textlink"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/pricing/rate-card.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('rate_card_download', { location: 'hero' })}
            data-testid="pricing-download-pdf"
          >
            <Download size={15} /> Download the full pricing PDF
          </a>
          <Link
            className="pp-hero-textlink"
            to="/quote"
            onClick={() => trackEvent('quote_builder_click', { location: 'hero' })}
            data-testid="pricing-quote-link"
          >
            <FileText size={15} /> Or build an instant quote
          </Link>
        </div>
      </div>
    </section>
  );
}

function Intro() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">MORE THAN JUST A <em>WEBSITE BUILD.</em></h2>
          <p className="pp-lead">
            A successful website should do more than look good. It should communicate clearly, build
            trust, make your business easier to understand and guide visitors towards taking action.
          </p>
          <p className="pp-lead">
            Every Boost! website project combines thoughtful structure, responsive design, technical
            setup and practical marketing experience.
          </p>
          <p className="pp-lead">
            Our packages provide a useful starting point, but every project is reviewed and scoped
            individually before work begins.
          </p>
        </div>
      </div>
    </section>
  );
}

function PackageCard({ pkg }) {
  return (
    <article className={`pp-card ${pkg.popular ? 'pp-card-popular' : ''}`} data-testid={`pricing-card-${pkg.id}`}>
      {pkg.popular && <span className="pp-popular-badge">Most Popular</span>}
      <h3 className="pp-card-title pp-display">{pkg.title}</h3>
      <p className="pp-card-ideal">{pkg.ideal}</p>
      <span className="pp-card-price-from">Starting from</span>
      <div className="pp-card-price">{pkg.priceEx} <small>+ VAT</small></div>
      <p className="pp-card-inc">From {pkg.priceInc} including VAT</p>
      <span className="pp-card-delivery"><Clock size={14} /> Typical delivery: {pkg.delivery}</span>
      <p className="pp-card-desc">{pkg.description}</p>
      <ul className="pp-card-list">
        {pkg.inclusions.map((item) => (
          <li key={item}><Check size={15} /> {item}</li>
        ))}
      </ul>
      {pkg.note && <p className="pp-card-note">{pkg.note}</p>}
      <button
        className={`pp-btn ${pkg.popular ? 'pp-btn-primary' : 'pp-btn-outline'}`}
        onClick={() => { trackEvent('package_cta_click', { package: pkg.id }); scrollToEnquiry(); }}
        data-testid={`pricing-cta-${pkg.id}`}
      >
        {pkg.cta}
      </button>
    </article>
  );
}

function Packages() {
  return (
    <section className="pp-section" id="packages">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">TRANSPARENT <em>STARTING PRICES.</em></h2>
          <p className="pp-lead">
            Four clear starting points, individually scoped around your goals. Every price shown is a
            “from” price, subject to project requirements.
          </p>
        </div>
        <div className="pp-cards">
          {PRICING_PACKAGES.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Bespoke() {
  return (
    <section className="pp-section" style={{ paddingTop: 0 }}>
      <div className="pp-container">
        <div className="pp-bespoke" data-testid="pricing-bespoke">
          <div>
            <h3 className="pp-display">NEED SOMETHING MORE <span style={{ color: '#FF4D94' }}>AMBITIOUS?</span></h3>
            <p>
              We also design and develop larger websites, customer portals, booking systems, membership
              platforms, interactive tools, dashboards and bespoke digital experiences.
            </p>
          </div>
          <div className="pp-bespoke-price">
            <span className="pp-card-price-from" style={{ color: '#8A8FB8' }}>Bespoke digital projects</span>
            <div className="pp-card-price">From {BESPOKE.priceEx} <small style={{ color: '#B9BDDD' }}>+ VAT</small></div>
            <p className="pp-card-inc">Including VAT: from {BESPOKE.priceInc}</p>
            <span className="pp-card-delivery"><Clock size={14} /> Typical delivery: {BESPOKE.delivery}</span>
            <div style={{ marginTop: 18 }}>
              <button
                className="pp-btn pp-btn-primary"
                onClick={() => { trackEvent('package_cta_click', { package: 'bespoke' }); scrollToEnquiry(); }}
                data-testid="pricing-cta-bespoke"
              >
                {BESPOKE.cta}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ComparisonTable() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">PRICING AT A <em>GLANCE.</em></h2>
        </div>
        <div className="pp-table-wrap" data-testid="pricing-table">
          <table className="pp-table">
            <thead>
              <tr>
                <th>Package</th>
                <th>Price excluding VAT</th>
                <th>Price including VAT</th>
                <th>Typical delivery</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.name} className={row.popular ? 'pp-row-popular' : ''}>
                  <td>
                    {row.name}
                    {row.popular && <span className="pp-tag">Most Popular</span>}
                  </td>
                  <td>{row.ex}</td>
                  <td>{row.inc}</td>
                  <td>{row.delivery}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pp-notice" data-testid="pricing-notice">
          <h3 className="pp-display">EVERY PROJECT IS DIFFERENT.</h3>
          <p>The prices shown are starting points based on typical project requirements.</p>
          <p>
            Your final proposal will reflect the number of pages, content requirements, functionality,
            integrations, ecommerce requirements and the level of creative or strategic support involved.
          </p>
          <p>
            Before confirming a fee, we will discuss your objectives, review the required functionality
            and provide a clearly defined scope of work.
          </p>
          <p>
            There are no hidden build fees. Any optional or additional work will be agreed before it begins.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function PricingPage() {
  const [hideSticky, setHideSticky] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 250);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [location.hash]);

  useEffect(() => {
    const target = document.getElementById('enquiry');
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setHideSticky(entry.isIntersecting),
      { rootMargin: '0px 0px -20% 0px' }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pricing-page" data-testid="pricing-page">
      <PageHead />
      <PricingHeader />
      <PricingSubNav />
      <main>
        <Hero />
        <Intro />
        <Packages />
        <Bespoke />
        <BundleBanner />
        <ComparisonTable />
        <PricingSections />
        <PricingEnquiry />
      </main>
      <footer className="pp-footer">
        <div className="pp-footer-inner">
          <span>© {new Date().getFullYear()} BOOST! Design &amp; Marketing — {BRAND.address}</span>
          <span><a href="/">boostmarketing.agency</a> · <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a></span>
        </div>
      </footer>
      <div className={`pp-sticky-cta${hideSticky ? ' pp-sticky-cta-hidden' : ''}`}>
        <button
          className="pp-btn pp-btn-primary"
          onClick={() => { trackEvent('proposal_request_click', { location: 'sticky_mobile' }); scrollToEnquiry(); }}
          data-testid="pricing-sticky-cta"
        >
          Request a website proposal
        </button>
      </div>
      <BackToTop />
    </div>
  );
}
